import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useDateFormatter } from '../../../../hooks/useDateFormatter';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface TrendDataPoint {
  date: string;
  new: number;
  assessment: number;
  rca: number;
  resolved: number;
  closed: number;
  cancelled: number;
}

interface ProblemTrendChartProps {
  data: TrendDataPoint[];
  onPointClick?: (date: string) => void;
}

const ProblemTrendChart: React.FC<ProblemTrendChartProps> = ({ data, onPointClick }) => {
  const { formatDate } = useDateFormatter();

  if (data.length === 0) {
    return (
      <div id="chart-problem-trend" className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Problem Trend Over Time</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No trend data available for the selected period
        </div>
      </div>
    );
  }

  const labels = data.map(d => formatDate(d.date + 'T00:00:00'));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'New',
        data: data.map(d => d.new),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Assessment',
        data: data.map(d => d.assessment),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'RCA',
        data: data.map(d => d.rca),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Resolved',
        data: data.map(d => d.resolved),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Closed',
        data: data.map(d => d.closed),
        borderColor: 'rgb(107, 114, 128)',
        backgroundColor: 'rgba(107, 114, 128, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: { size: 11 }
        }
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              const index = items[0].dataIndex;
              const dateStr = data[index]?.date;
              if (dateStr) {
                return formatDate(dateStr + 'T00:00:00');
              }
            }
            return '';
          },
          label: (context: any) => {
            return `${context.dataset.label}: ${context.raw} problems`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 10 }
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onPointClick) {
        const index = elements[0].index;
        const dateStr = data[index]?.date;
        if (dateStr) {
          onPointClick(dateStr);
        }
      }
    },
  };

  return (
    <div id="chart-problem-trend" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Problem Trend Over Time</h3>
        <span className="text-xs text-gray-500">Click point to view details</span>
      </div>
      <div className="h-72">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ProblemTrendChart;
